import React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../utils/cn";

// Shared cap for the object-attribute tooltip table across tiles — "Zeige X
// von Y" (showingCount) covers the rest; totalCount is always the full
// filtered count regardless of this cap (backend computes it pre-pagination).
export const TOOLTIP_OBJECT_LIMIT = 100;

/**
 * Object-attribute table content for a configurable tooltip: "Object ID" +
 * one column per configured attribute, capped rows, a "Showing X of Y"
 * footer when truncated. Shared by every tile whose tooltip lists many
 * objects (one row each) — not a fit for a tooltip showing a single hovered
 * object's own attributes as key/value pairs (e.g. Gantt Chart).
 */
const ObjectAttributeTable = ({
  attributes = [],
  rows = [],
  totalCount = 0,
  loading = false,
  error = null,
  formatCellValue,
  className = "",
  // Set false when this is the only content in the card (nothing precedes
  // it, e.g. Resource Utilization Chart's click panel) — true when it
  // follows a summary line inside the same card.
  leadingDivider = true,
}) => {
  const { t } = useTranslation("tiles");

  return (
    <>
      {leadingDivider && <div className="border-t border-[#f1f5f9]" />}
      {loading ? (
        <div className="px-3.5 py-3 text-[0.72rem] text-[#64748b]">{t("objectTable.loading")}</div>
      ) : error ? (
        <div className="px-3.5 py-3 text-[0.72rem] text-red-600">{t("objectTable.error")}</div>
      ) : rows.length === 0 ? (
        <div className="px-3.5 py-3 text-[0.72rem] text-[#64748b]">{t("objectTable.empty")}</div>
      ) : (
        <>
          {/* Width capped here — not per call site — so every consumer (the
              card container above it auto-sizes via w-auto/max-w-none) gets
              the same bound regardless of how many attribute columns are
              configured; overflow-auto adds horizontal scroll for the rest. */}
          <div className={cn("max-h-56 max-w-[min(80vw,560px)] overflow-auto", className)}>
            <table className="text-[0.72rem] border-collapse">
              <thead className="sticky top-0 bg-white">
                <tr>
                  <th className="px-3.5 py-1.5 text-left font-semibold text-[#0f172a] border-b border-[#f1f5f9] whitespace-nowrap">
                    {t("objectTable.objectIdColumn")}
                  </th>
                  {attributes.map((attr) => (
                    <th
                      key={attr}
                      className="px-3.5 py-1.5 text-left font-semibold text-[#0f172a] border-b border-[#f1f5f9] whitespace-nowrap"
                    >
                      {attr}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.object_id}>
                    <td className="px-3.5 py-1 text-[#1e293b] whitespace-nowrap">{row.object_id}</td>
                    {attributes.map((attr) => (
                      <td key={attr} className="px-3.5 py-1 text-[#1e293b] whitespace-nowrap">
                        {formatCellValue ? formatCellValue(attr, row.attributes?.[attr]) : row.attributes?.[attr]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalCount > rows.length && (
            <div className="px-3.5 py-1.5 text-[0.66rem] text-[#64748b] border-t border-[#f1f5f9] whitespace-nowrap">
              {t("objectTable.showingCount", { shown: rows.length, total: totalCount.toLocaleString() })}
            </div>
          )}
        </>
      )}
    </>
  );
};

export default ObjectAttributeTable;
